import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Text, Badge, Button, Icon } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { isActive } from 'domain/plans';
import { RowChild, RowBuffer, Container, Row } from 'styles';
import {
  isActive as isProductActive,
  isPending,
} from 'domain/product-statuses';
import { ReactivatePlan } from 'components/modals/reactivate-plan/modal.component';
import { DashboardFunctions } from 'pages/dashboard/dashboard.page';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { REACTIVATION_SOURCE } from 'components/modals/reactivate-plan/modal.constants';
import { DashboardPlan } from '../dashboard.api';
import { StyledIconButton, ButtonWrapper } from './dashboard-row.style';
import { VnmStatus } from './vnm-status';

interface Props {
  plan: DashboardPlan;
  isSMB?: boolean;
}

const getCopy = copyFor('dashboard.standardDashboard.listItem');

const RowContent = ({
  onClick,
  justifyContent,
  children,
}: {
  onClick?: () => void;
  justifyContent?: string;
  children: React.ReactNode;
}) => {
  return (
    <Row padding="0" justifyContent="flex-end" onClick={onClick}>
      <RowBuffer width="32px" xsmall={{ width: '0' }} />
      <RowChild
        padding={{ bottom: '32px', top: '32px' }}
        xsmall={{ padding: { bottom: '24px', top: '24px' } }}
        justifyContent={justifyContent}
      >
        {children}
      </RowChild>
    </Row>
  );
};

export const DashboardRow = ({ plan, isSMB }: Props) => {
  const history = useHistory();
  const { reactivationEligibility } = plan;

  const { dashboardPlan } = routes({
    planId: plan.id,
  });
  const [reactivatePlanModalIsOpen, setReactivatePlanModalIsOpen] =
    useState(false);
  const planName = plan.displayName;
  const utilityAccount = plan.utilityAccounts[0];
  const utilityDisplayName = utilityAccount?.utility.displayName;
  const utilityAccountNumber = utilityAccount?.accountNumber || '';
  const utilityAccountHighlights = utilityAccount?.highlights?.length
    ? utilityAccount.highlights
    : [];
  const address = utilityAccount?.displayServiceAddress;
  const statement = plan.mostRecentStatement;
  const shouldShowNewBillBadge =
    isActive(plan.status) && statement?.status === 'issued';
  const percentSavings = statement?.lineItemSummary.headlineSolarSavingsRate
    ? statement?.lineItemSummary.headlineSolarSavingsRate
    : undefined;

  const subscription = plan.vnmProductSubscription?.vnmSubscription;
  const solarProject = subscription?.vnmSolarProject;
  const vnmStatus = plan.productStatuses.vnm.status;
  const isActiveProductStatus = isProductActive(vnmStatus);
  const isPendingProductStatus = isPending(vnmStatus);
  const context = useContext(DashboardFunctions);
  const flag = useFlags();

  const toggleReactivationModal = event => {
    if (!event.refreshDashboard) event?.stopPropagation();
    context?.setRefreshDashboardComponent(event.refreshDashboard || false);
    setReactivatePlanModalIsOpen(!reactivatePlanModalIsOpen);
  };

  return (
    <RowContent onClick={() => history.push(dashboardPlan)}>
      <Container flex direction="column" grow={1}>
        <Container flex justifyContent="space-between" alignItems="center">
          <Container flex alignItems="center">
            <Text
              className="notranslate"
              textStyle="heading600"
              tag="h3"
              margin={{ right: '16px' }}
            >
              {planName}
            </Text>
            {shouldShowNewBillBadge && (
              <Badge color="primary" backgroundColor="accent2">
                {getCopy('newBill')}
              </Badge>
            )}
          </Container>
          <StyledIconButton
            aria-label={getCopy('ariaLabel', { planName })}
            onClick={() => history.push(dashboardPlan)}
          />
        </Container>
        {utilityDisplayName && (
          <Text
            data-testid="subtitle"
            textStyle="paragraph400"
            color="secondary"
          >
            {getCopy('subtitle', {
              utilityDisplayName,
              utilityAccountNumber:
                isSMB && utilityAccountNumber ? `#${utilityAccountNumber}` : '',
              address: address && ` • ${address}`,
            })}
            {Boolean(utilityAccountHighlights) &&
              utilityAccountHighlights.map(highlight => (
                <Badge
                  data-testid="badge-highlight"
                  color="primary"
                  backgroundColor="error"
                >
                  <Icon color="error" icon="Flag" />
                  {highlight}
                </Badge>
              ))}
          </Text>
        )}
        {isActive(plan.status) &&
          (isActiveProductStatus || isPendingProductStatus) && (
            <VnmStatus
              firstStatementWithSolarSavings={
                statement?.firstStatementWithSolarSavings
              }
              isPendingProductStatus={isPendingProductStatus}
              subscriptionStatus={subscription?.status}
              projectStartDate={solarProject?.startDate}
              projectName={solarProject?.name}
              percentSavings={percentSavings}
              isSMB={isSMB}
            />
          )}
        {flag.lbReactivationPlanEnabled &&
          !plan.isExclusiveMembershipPlan &&
          reactivationEligibility.eligible &&
          !isActive(plan.status) && (
            <ButtonWrapper>
              <Button
                color="primary"
                backgroundless={true}
                size="small"
                onClick={toggleReactivationModal}
                data-testid="reactivate-plan-button"
              >
                <Icon
                  color="primary"
                  icon="LightningBolt"
                  margin={{ right: '8px' }}
                />
                {getCopy('reactivateButton')}
              </Button>
            </ButtonWrapper>
          )}
        {reactivatePlanModalIsOpen && (
          <ReactivatePlan
            planId={plan.id}
            planNickname={plan.displayName}
            onClose={e => toggleReactivationModal(e)}
            source={REACTIVATION_SOURCE.DASHBOARD}
          />
        )}
      </Container>
    </RowContent>
  );
};
